import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Grid,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  UploadFile as UploadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

interface Player {
  id: string;
  playerIdCode: string;
  firstName: string;
  lastName: string;
  email: string;
  primarySport?: string;
  secondarySport?: string;
  grade?: string;
  section?: string;
  level?: string;
}

interface PlayerManagementProps {
  academyId: string;
}

const PlayerManagement: React.FC<PlayerManagementProps> = ({ academyId }) => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [filteredPlayers, setFilteredPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [searchingPlayer, setSearchingPlayer] = useState(false);
  const [foundPlayer, setFoundPlayer] = useState<any>(null);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    playerIdCode: '',
    dateOfBirth: '',
    gender: 'male',
    country: '',
    primarySport: '',
    secondarySport: '',
    grade: '',
    section: '',
    level: 'beginner',
    ageCategory: '18+', // 'below-18' or '18+'
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    parentRelationship: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // Filter and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSport, setFilterSport] = useState<string>('');
  const [filterGrade, setFilterGrade] = useState<string>('');
  const [filterLevel, setFilterLevel] = useState<string>('');

  const sports = ['Badminton', 'Cricket', 'Tennis', 'Football', 'Athletics'];
  const levels = ['beginner', 'intermediate', 'advanced'];
  const genders = ['male', 'female', 'other'];

  useEffect(() => {
    if (academyId) {
      loadPlayers();
    } else {
      console.error('[PlayerManagement] Academy ID is undefined');
    }
  }, [academyId]);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...players];

    // Search by name or player ID
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(player => {
        const fullName = `${player.firstName} ${player.lastName}`.toLowerCase();
        const playerId = (player.playerIdCode || '').toLowerCase();
        return fullName.includes(query) || playerId.includes(query);
      });
    }

    // Filter by sport
    if (filterSport) {
      filtered = filtered.filter(player => 
        player.primarySport === filterSport || player.secondarySport === filterSport
      );
    }

    // Filter by grade
    if (filterGrade) {
      filtered = filtered.filter(player => player.grade === filterGrade);
    }

    // Filter by level
    if (filterLevel) {
      filtered = filtered.filter(player => player.level === filterLevel);
    }

    setFilteredPlayers(filtered);
  }, [players, searchQuery, filterSport, filterGrade, filterLevel]);

  const loadPlayers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/academies/${academyId}/players`);
      const result = await response.json();
      
      if (result.success) {
        const loadedPlayers = result.data || [];
        setPlayers(loadedPlayers);
        setFilteredPlayers(loadedPlayers);
      } else {
        setError(result.message || 'Failed to load players');
      }
    } catch (err: any) {
      console.error('Error loading players:', err);
      setError('Failed to load players');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchByPlayerId = async () => {
    if (!formData.playerIdCode) {
      toast.error('Please enter a Player ID');
      return;
    }

    setSearchingPlayer(true);
    try {
      const response = await fetch(`/api/players/by-id/${formData.playerIdCode}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        const player = result.data;
        
        // Determine age category for parent fields visibility
        const ageCategory = player.ageCategory || (player.dateOfBirth ? 
          (new Date().getFullYear() - new Date(player.dateOfBirth).getFullYear() < 18 ? 'below-18' : '18+') 
          : '18+');
        
        setFormData({
          ...formData,
          email: player.email || formData.email,
          firstName: player.firstName || formData.firstName,
          lastName: player.lastName || formData.lastName,
          dateOfBirth: player.dateOfBirth || formData.dateOfBirth,
          gender: player.gender || formData.gender,
          country: player.country || formData.country,
          primarySport: player.primarySport || formData.primarySport,
          secondarySport: player.secondarySport || formData.secondarySport,
          grade: player.grade || formData.grade,
          section: player.section || formData.section,
          level: player.level || player.skillLevel || formData.level,
          ageCategory: ageCategory,
          // Map parent fields from API response
          // API returns: parentEmail, parentPhone, fatherName, motherName, parentCountry
          // Form expects: parentName, parentEmail, parentPhone, parentRelationship
          parentName: player.parentName || player.fatherName || player.motherName || formData.parentName,
          parentEmail: player.parentEmail || formData.parentEmail,
          parentPhone: player.parentPhone || formData.parentPhone,
          parentRelationship: player.parentRelationship || (player.fatherName ? 'Father' : player.motherName ? 'Mother' : formData.parentRelationship),
        });
        setFoundPlayer(player);
        toast.success('Player information loaded successfully');
      } else {
        toast.error(result.message || 'Player not found');
        setFoundPlayer(null);
      }
    } catch (err: any) {
      console.error('Error searching player:', err);
      toast.error('Failed to search player');
      setFoundPlayer(null);
    } finally {
      setSearchingPlayer(false);
    }
  };

  const handleAddPlayer = async () => {
    if (!formData.email || !formData.firstName || !formData.lastName) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!editingPlayer && formData.ageCategory === 'below-18') {
      if (!formData.parentName || !formData.parentEmail || !formData.parentPhone || !formData.parentRelationship) {
        toast.error('Please fill in all parent/guardian information for players below 18');
        return;
      }
    }

    setLoading(true);
    try {
      const isEdit = !!editingPlayer;
      const url = isEdit 
        ? `/api/academies/${academyId}/players/${editingPlayer.id}`
        : `/api/academies/${academyId}/players`;
      
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          playerIdCode: formData.playerIdCode || undefined,
          dateOfBirth: formData.dateOfBirth || undefined,
          gender: formData.gender,
          country: formData.country || undefined,
          primarySport: formData.primarySport || undefined,
          secondarySport: formData.secondarySport || undefined,
          grade: formData.grade || undefined,
          section: formData.section || undefined,
          level: formData.level,
          ageCategory: formData.ageCategory,
          parentName: formData.ageCategory === 'below-18' ? (formData.parentName || undefined) : undefined,
          parentEmail: formData.ageCategory === 'below-18' ? (formData.parentEmail || undefined) : undefined,
          parentPhone: formData.ageCategory === 'below-18' ? (formData.parentPhone || undefined) : undefined,
          parentRelationship: formData.ageCategory === 'below-18' ? (formData.parentRelationship || undefined) : undefined,
        }),
      });

      // Check if response is ok before parsing JSON
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        if (isEdit) {
          toast.success('Player updated successfully');
        } else {
          toast.success(result.data?.existed ? 'Player added to academy successfully' : 'Player created and added to academy successfully');
        }
        setDialogOpen(false);
        setEditingPlayer(null);
        resetForm();
        await loadPlayers(); // Wait for players to reload before closing
      } else {
        // Show error message - this will handle duplicate player warnings
        const errorMessage = result.message || `Failed to ${isEdit ? 'update' : 'add'} player`;
        toast.error(errorMessage);
      }
    } catch (err: any) {
      console.error(`Error ${editingPlayer ? 'updating' : 'adding'} player:`, err);
      const errorMessage = err?.message || `Failed to ${editingPlayer ? 'update' : 'add'} player`;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('academyId', academyId);

      const response = await fetch(`/api/academies/${academyId}/players/upload-excel`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`Successfully imported ${result.data.imported || 0} players`);
        if (result.data.errors && result.data.errors.length > 0) {
          console.warn('Import errors:', result.data.errors);
        }
        setUploadDialogOpen(false);
        setSelectedFile(null);
        loadPlayers();
      } else {
        toast.error(result.message || 'Failed to upload players');
      }
    } catch (err: any) {
      console.error('Error uploading players:', err);
      toast.error('Failed to upload players');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlayer = (player: Player) => {
    setLoading(false); // Reset loading state when opening edit dialog
    setEditingPlayer(player);
    setFormData({
      email: player.email,
      firstName: player.firstName,
      lastName: player.lastName,
      playerIdCode: player.playerIdCode || '',
      dateOfBirth: '',
      gender: 'male',
      country: '',
      primarySport: player.primarySport || '',
      secondarySport: player.secondarySport || '',
      grade: player.grade || '',
      section: player.section || '',
      level: player.level || 'beginner',
      ageCategory: '18+', // Default for editing (can be updated if needed)
      parentName: '',
      parentEmail: '',
      parentPhone: '',
      parentRelationship: '',
    });
    setDialogOpen(true);
  };

  const handleDeletePlayer = async (playerId: string) => {
    if (!window.confirm('Are you sure you want to remove this player from this academy? Note: The player will still exist and can be added to other academies.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/academies/${academyId}/players/${playerId}`, {
        method: 'DELETE',
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Player removed from academy successfully');
        loadPlayers();
      } else {
        toast.error(result.message || 'Failed to remove player');
      }
    } catch (err: any) {
      console.error('Error deleting player:', err);
      toast.error('Failed to remove player');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      playerIdCode: '',
      dateOfBirth: '',
      gender: 'male',
      country: '',
      primarySport: '',
      secondarySport: '',
      grade: '',
      section: '',
      level: 'beginner',
      ageCategory: '18+',
      parentName: '',
      parentEmail: '',
      parentPhone: '',
      parentRelationship: '',
    });
    setFoundPlayer(null);
    setEditingPlayer(null);
    setLoading(false); // Reset loading state when form is reset
  };

  if (loading && players.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Player Management</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Players can be part of multiple academies. This page shows players for this academy only.
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
            sx={{ mr: 2 }}
          >
            Upload Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Add Player
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filter and Search Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search by Name or Player ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              placeholder="Enter name or player ID..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sport</InputLabel>
              <Select
                value={filterSport}
                label="Sport"
                onChange={(e) => setFilterSport(e.target.value)}
              >
                <MenuItem value="">All Sports</MenuItem>
                {sports.map((sport) => (
                  <MenuItem key={sport} value={sport}>
                    {sport}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Grade</InputLabel>
              <Select
                value={filterGrade}
                label="Grade"
                onChange={(e) => setFilterGrade(e.target.value)}
              >
                <MenuItem value="">All Grades</MenuItem>
                {Array.from(new Set(players.map(p => p.grade).filter(Boolean))).sort().map((grade) => (
                  <MenuItem key={grade} value={grade}>
                    {grade}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Level</InputLabel>
              <Select
                value={filterLevel}
                label="Level"
                onChange={(e) => setFilterLevel(e.target.value)}
              >
                <MenuItem value="">All Levels</MenuItem>
                {levels.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchQuery('');
                setFilterSport('');
                setFilterGrade('');
                setFilterLevel('');
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Player ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Grade</TableCell>
              <TableCell>Section</TableCell>
              <TableCell>Level</TableCell>
              <TableCell>Primary Sport</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPlayers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {players.length === 0 
                      ? 'No players found. Add your first player to get started.'
                      : 'No players match the current filters. Try adjusting your search criteria.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredPlayers.map((player) => (
                <TableRow key={player.id}>
                  <TableCell>{player.playerIdCode}</TableCell>
                  <TableCell>{`${player.firstName} ${player.lastName}`}</TableCell>
                  <TableCell>{player.email}</TableCell>
                  <TableCell>{player.grade || 'N/A'}</TableCell>
                  <TableCell>{player.section || 'N/A'}</TableCell>
                  <TableCell>
                    {player.level && (
                      <Chip
                        label={player.level.charAt(0).toUpperCase() + player.level.slice(1)}
                        size="small"
                        color={
                          player.level === 'advanced' ? 'success' :
                          player.level === 'intermediate' ? 'warning' : 'default'
                        }
                      />
                    )}
                  </TableCell>
                  <TableCell>{player.primarySport || 'N/A'}</TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleEditPlayer(player)}
                      title="Edit Player"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeletePlayer(player.id)}
                      title="Remove Player from Academy"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Player Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => { 
          setLoading(false); // Ensure loading is reset when dialog closes
          setDialogOpen(false); 
          resetForm(); 
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>{editingPlayer ? 'Edit Player' : 'Add Player'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
                {!editingPlayer && (
                  <>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                        <TextField
                          fullWidth
                          label="EncycGames Player ID (Optional)"
                          value={formData.playerIdCode}
                          onChange={(e) => setFormData({ ...formData, playerIdCode: e.target.value })}
                          helperText="If player is already registered, enter their Player ID here"
                        />
                        <Button
                          variant="outlined"
                          onClick={handleSearchByPlayerId}
                          disabled={!formData.playerIdCode || searchingPlayer}
                        >
                          {searchingPlayer ? 'Searching...' : 'Search & Load'}
                        </Button>
                      </Box>
                    </Grid>
                    
                    {/* Age Category Tabs (similar to player registration) */}
                    <Grid item xs={12} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <ToggleButtonGroup
                          value={formData.ageCategory}
                          exclusive
                          onChange={(e, newValue) => {
                            if (newValue !== null) {
                              setFormData({ ...formData, ageCategory: newValue });
                            }
                          }}
                          aria-label="age category"
                          sx={{
                            '& .MuiToggleButton-root': {
                              px: 4,
                              py: 1.5,
                              fontSize: '1rem',
                            },
                          }}
                        >
                          <ToggleButton value="18+" aria-label="18+">
                            Regular Player (18+)
                          </ToggleButton>
                          <ToggleButton value="below-18" aria-label="below 18">
                            Player Below 18
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                      <Typography variant="body2" color="text.secondary" align="center">
                        {formData.ageCategory === 'below-18'
                          ? 'Registering for a player below 18 years. Parent/Guardian information required.'
                          : 'Registering as a regular player (18 years or above).'}
                      </Typography>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {formData.ageCategory === 'below-18' ? (
                      <>
                        {/* Below 18 Registration Form */}
                        <Grid item xs={12}>
                          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                            Player/Child Information
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Name of your Child/Player *"
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Last Name (Optional)"
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Date of Birth *"
                            type="date"
                            value={formData.dateOfBirth}
                            onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>Gender *</InputLabel>
                            <Select
                              value={formData.gender}
                              label="Gender *"
                              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                            >
                              {genders.map((g) => (
                                <MenuItem key={g} value={g}>
                                  {g.charAt(0).toUpperCase() + g.slice(1)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 2 }}>
                          <Divider />
                          <Typography variant="h6" sx={{ mt: 2, mb: 2, color: 'primary.main' }}>
                            Parent/Guardian Information
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Parent Email Address *"
                            type="email"
                            value={formData.parentEmail}
                            onChange={(e) => setFormData({ ...formData, parentEmail: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Parent Phone Number *"
                            value={formData.parentPhone}
                            onChange={(e) => setFormData({ ...formData, parentPhone: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Country *"
                            value={formData.country}
                            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Parent/Guardian Name *"
                            value={formData.parentName}
                            onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Relationship *"
                            value={formData.parentRelationship}
                            onChange={(e) => setFormData({ ...formData, parentRelationship: e.target.value })}
                            placeholder="e.g., Father, Mother, Guardian"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Email *"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            helperText="This will be used as the player's account email"
                          />
                        </Grid>
                      </>
                    ) : (
                      <>
                        {/* Regular Player Registration Form (18+) */}
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="First Name *"
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Last Name *"
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Email Address *"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Date of Birth *"
                            type="date"
                            value={formData.dateOfBirth}
                            onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>Gender *</InputLabel>
                            <Select
                              value={formData.gender}
                              label="Gender *"
                              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                            >
                              {genders.map((g) => (
                                <MenuItem key={g} value={g}>
                                  {g.charAt(0).toUpperCase() + g.slice(1)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    )}
                  </>
                )}
            {editingPlayer && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Editing: <strong>{editingPlayer.firstName} {editingPlayer.lastName}</strong> ({editingPlayer.email})
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Grade"
                value={formData.grade}
                onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                placeholder="e.g., 5, 6, 7"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Section"
                value={formData.section}
                onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                placeholder="e.g., A, B, C"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Level *</InputLabel>
                <Select
                  value={formData.level}
                  label="Level *"
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                >
                  {levels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Primary Sport</InputLabel>
                <Select
                  value={formData.primarySport}
                  label="Primary Sport"
                  onChange={(e) => setFormData({ ...formData, primarySport: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  {sports.map((sport) => (
                    <MenuItem key={sport} value={sport}>
                      {sport}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Secondary Sport</InputLabel>
                <Select
                  value={formData.secondarySport}
                  label="Secondary Sport"
                  onChange={(e) => setFormData({ ...formData, secondarySport: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  {sports.map((sport) => (
                    <MenuItem key={sport} value={sport}>
                      {sport}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => { 
              setLoading(false); // Ensure loading is reset when canceling
              setDialogOpen(false); 
              resetForm(); 
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleAddPlayer} variant="contained" disabled={loading}>
            {loading ? (editingPlayer ? 'Updating...' : 'Adding...') : (editingPlayer ? 'Update Player' : 'Add Player')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Excel Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => { setUploadDialogOpen(false); setSelectedFile(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Players from Excel</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload an Excel file (.xlsx) with player information. The file should have columns:
              Email, First Name, Last Name, Player ID (optional), Date of Birth, Gender, Country, Primary Sport, Secondary Sport, Grade, Section, Level (beginner/intermediate/advanced), Age Category (18+ or below-18).
              <br /><br />
              For players below 18, also include: Parent Name, Parent Email, Parent Phone, Parent Relationship.
            </Typography>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              style={{ marginTop: 16 }}
            />
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {selectedFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialogOpen(false); setSelectedFile(null); }}>Cancel</Button>
          <Button onClick={handleFileUpload} variant="contained" disabled={loading || !selectedFile}>
            {loading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlayerManagement;
